import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Newspaper, ChevronRight, Calendar, Star, ArrowRight } from 'lucide-react';
import { API } from '@/context/AuthContext';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

const formatDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

/**
 * Compact "Latest from BCI" strip — top 3 articles (featured-first) for HomePage.
 * Silent on no-content (returns null) so the home page doesn't show an empty section.
 */
const LatestNewsStrip = () => {
  const [news, setNews] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [n, c] = await Promise.all([
          axios.get(`${API}/news?is_active=true&limit=6`),
          axios.get(`${API}/news/categories`),
        ]);
        setNews((n.data || []).slice(0, 3));
        setCats(c.data || []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  if (!news.length) return null;

  const catMap = Object.fromEntries(cats.map((c) => [c.key, c]));

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden bg-[#0A0A0B]">
      <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ background: 'radial-gradient(ellipse at top right, rgba(212,175,55,0.18), transparent 55%)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-2">— Editorial —</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-wide" style={{ fontFamily: 'Cinzel, serif' }}>
              Latest from <span className="text-[#D4AF37]">BCI</span>
            </h2>
          </div>
          <Link to="/news" className="inline-flex items-center gap-1 text-sm text-gray-300 hover:text-[#D4AF37] transition" data-testid="home-news-all">
            All news <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {news.map((item, i) => {
            const cat = catMap[item.category] || { label: item.category, accent: '#D4AF37' };
            return (
              <motion.div
                key={item.id}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <Link
                  to={`/news/${item.slug || item.id}`}
                  className="block bg-[#0F0F10] border border-white/5 hover:border-[#D4AF37]/40 rounded-2xl overflow-hidden transition-all duration-500 hover:translate-y-[-4px] hover:shadow-[0_18px_50px_-12px_rgba(212,175,55,0.18)] group"
                  data-testid={`home-news-card-${i}`}
                >
                  <div className="aspect-[16/9] relative overflow-hidden" style={{ background: `linear-gradient(135deg, #0D1B3F 0%, #0A0A0B 60%, ${cat.accent}33 100%)` }}>
                    {(item.cover_image_url || item.image_url) ? (
                      <img src={item.cover_image_url || item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Newspaper className="w-12 h-12 opacity-25" style={{ color: cat.accent }} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full backdrop-blur-md" style={{ background: `${cat.accent}25`, color: '#fff', border: `1px solid ${cat.accent}66` }}>
                        {cat.label}
                      </span>
                      {item.is_featured && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#D4AF37]/30 text-white border border-[#D4AF37]/60 flex items-center gap-1 backdrop-blur-md">
                          <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" /> Featured
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] text-gray-500 mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(item.published_at || item.created_at)}</p>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition" style={{ fontFamily: 'Cinzel, serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3">{item.summary || item.content}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#D4AF37]">
                      Read <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LatestNewsStrip;
